import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Loader2, UserPlus, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export function ClientsListPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tf = (fr, en) => language === 'fr' ? fr : en;

  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const STATUS_MAP = {
    signe: { label: tf('Signe', 'Signed'), color: 'bg-blue-500' },
    travaux_en_cours: { label: tf('Travaux en cours', 'Work in progress'), color: 'bg-orange-500' },
    complete: { label: tf('Complete', 'Completed'), color: 'bg-green-500' },
    annule: { label: tf('Annule', 'Cancelled'), color: 'bg-red-500' },
  };

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/clients', { params: { search } });
      setClients(data.clients);
      setTotal(data.total);
    } catch {
      toast.error(tf('Erreur de chargement', 'Loading error'));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { loadClients(); }, [loadClients]);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">
            {tf('Clients', 'Clients')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {tf(`${total} client${total !== 1 ? 's' : ''}`, `${total} client${total !== 1 ? 's' : ''}`)}
          </p>
        </div>
        <p className="text-sm text-muted-foreground italic" data-testid="clients-convert-hint">
          {tf('Convertissez un prospect pour ajouter un client', 'Convert a prospect to add a client')}
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={tf('Rechercher par nom, email, telephone...', 'Search by name, email, phone...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="clients-search"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : clients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <UserPlus className="h-12 w-12 mb-4 opacity-40" />
              <p className="font-medium">{tf('Aucun client', 'No clients')}</p>
              <p className="text-sm">{tf('Convertissez un prospect pour creer un client', 'Convert a prospect to create a client')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="clients-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{tf('Nom', 'Last name')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{tf('Prenom', 'First name')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">Email</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{tf('Telephone', 'Phone')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{tf('Statut', 'Status')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3 hidden lg:table-cell">{tf('Adresse', 'Address')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3 hidden lg:table-cell">{tf('Ville', 'City')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{tf('Date', 'Date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(c => {
                    const st = STATUS_MAP[c.status] || STATUS_MAP.signe;
                    return (
                      <tr
                        key={c.client_id}
                        className="border-b hover:bg-muted/20 cursor-pointer transition-colors"
                        onClick={() => navigate(`/clients/${c.client_id}`)}
                        data-testid={`client-row-${c.client_id}`}
                      >
                        <td className="p-3 font-medium text-sm">{c.last_name || '\u2014'}</td>
                        <td className="p-3 text-sm">{c.first_name || '\u2014'}</td>
                        <td className="p-3 text-sm">
                          {c.email ? (
                            <span className="flex items-center gap-1.5">
                              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                              {c.email}
                            </span>
                          ) : '\u2014'}
                        </td>
                        <td className="p-3 text-sm">
                          {c.phone ? (
                            <span className="flex items-center gap-1.5">
                              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                              {c.phone}
                            </span>
                          ) : '\u2014'}
                        </td>
                        <td className="p-3 text-sm">
                          <Badge className={`${st.color} text-white text-xs`} data-testid={`client-status-${c.client_id}`}>
                            {st.label}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm hidden lg:table-cell truncate max-w-[200px]">{c.address || '\u2014'}</td>
                        <td className="p-3 text-sm hidden lg:table-cell">{c.city || '\u2014'}</td>
                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(c.created_at).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
